import {useEffect, useRef, useState} from "react";
import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import {apiBindings} from "../../utils/constants";
import {get, post, postFile} from "../../utils/http.client";
import {prepareImageUpload} from "../../utils/file.utils";
import NumberInput from "../../elements/numberInput.element";
import {
    AVATAR_ELEMENT_TYPES, AVATAR_OFFSET_LIMIT, AVATAR_SCALE_MAX, AVATAR_SCALE_MIN, AVATAR_TYPE_LABELS,
    AvatarCatalogue, AvatarElement, AvatarElementType, AvatarPicks, elementLabel, normalizeCatalogue,
    OPTIONAL_AVATAR_TYPES,
} from "../../models/avatars.model";
import AvatarStage from "./avatarStage.element";
import {elementImageUrl, TransientFit} from "./avatarLayers.element";

// The Avatar tab: the global avatar element catalogue. NOT branch data — it lives
// in its own avatars.json blob, outside the branch history and export. Edits build
// a local draft with its own undo; Save writes it to the dev catalogue, and
// "Publish to Live" pushes what dev holds (plus its element images) to the live
// service. Tester+ for the tab and Save, Admin+ for Publish — matching SetAvatars.
//
// Laid out like the Images tab: the stage in the main area, and a sidebar with the
// element grid per slot on top and the selected element's details below.

const SIDEBAR_WIDTH = 380;
const THUMB_SIZE = 72;
const MAX_UNDO_DEPTH = 100;

type Selection = {type: AvatarElementType; id: number};
// How many player avatars name each element ("face" → id → count); undefined until
// loaded, null when the count could not be had.
type Usage = Record<string, Record<string, number>> | null | undefined;

// Read a just-selected image file's natural pixel dimensions locally (no network).
// Undefined if the file can't be decoded as an image.
async function readImageDimensions(file: File): Promise<{width: number; height: number} | undefined> {
    try {
        const bitmap = await createImageBitmap(file);
        const dimensions = {width: bitmap.width, height: bitmap.height};
        bitmap.close();
        return dimensions;
    } catch {
        return undefined;
    }
}

const sameCatalogue = (a: AvatarCatalogue, b: AvatarCatalogue) =>
    JSON.stringify({...a, version: 0}) === JSON.stringify({...b, version: 0});

// The first offered (not hidden) element of every required slot; optional slots left out.
function defaultPicks(catalogue: AvatarCatalogue): AvatarPicks {
    const picks: AvatarPicks = {};
    for (const type of AVATAR_ELEMENT_TYPES) {
        if (OPTIONAL_AVATAR_TYPES.includes(type)) continue;
        const first = catalogue.elements[type].find(e => !e.hidden) ?? catalogue.elements[type][0];
        if (first) picks[type] = first.id;
    }
    return picks;
}

function AvatarEditorView() {
    const {setNotification, setLoading, confirm} = useApp();
    const {canEditAvatars, canPublishAvatarsLive} = useAccess();
    const rootRef = useRef<HTMLDivElement>(null);

    // What the dev service holds, and the local draft built on it.
    const [saved, setSaved] = useState<AvatarCatalogue | undefined>(undefined);
    const [draft, setDraft] = useState<AvatarCatalogue | undefined>(undefined);
    const [fetchFailed, setFetchFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [usage, setUsage] = useState<Usage>(undefined);
    const [selected, setSelected] = useState<Selection | undefined>(undefined);
    const [picks, setPicks] = useState<AvatarPicks>({});
    const [transient, setTransient] = useState<TransientFit | undefined>(undefined);
    const [collapsed, setCollapsed] = useState<Set<AvatarElementType>>(new Set());
    const [dropTarget, setDropTarget] = useState<AvatarElementType | undefined>(undefined);
    const [uploading, setUploading] = useState<AvatarElementType | undefined>(undefined);
    const undoStack = useRef<AvatarCatalogue[]>([]);
    const redoStack = useRef<AvatarCatalogue[]>([]);
    // The latest draft, for edits that land after an await (uploads).
    const draftRef = useRef<AvatarCatalogue | undefined>(undefined);
    draftRef.current = draft;

    const dirty = !!saved && !!draft && !sameCatalogue(saved, draft);

    function fetchCatalogue() {
        setFetchFailed(false);
        fetch(`${apiBindings.api}getAvatars`, {cache: 'no-store'})
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(raw => {
                // The backend wraps the catalogue string in an OkObjectResult (matching
                // getAttributeManifest), so the body may be a JSON-stringified catalogue.
                const catalogue = normalizeCatalogue(typeof raw === 'string' ? JSON.parse(raw) : raw);
                setSaved(catalogue);
                setDraft(catalogue);
                setPicks(defaultPicks(catalogue));
                setSelected(undefined);
                undoStack.current = [];
                redoStack.current = [];
            })
            // NO empty-catalogue fallback: a save from an empty fallback would
            // wipe the real catalogue, so failure blocks editing instead.
            .catch(() => setFetchFailed(true));
    }

    function fetchUsage() {
        setUsage(undefined);
        get(`${apiBindings.api}avatarUsage`, undefined)
            .then(result => setUsage(result.success ? result.data : null));
    }

    useEffect(() => {
        if (!canEditAvatars) return;
        fetchCatalogue();
        fetchUsage();
        // Mount-only (and on gaining access); the fetchers aren't stable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEditAvatars]);

    // An unsaved draft would be lost with the page.
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    // ---- draft edits, each one undo step ----------------------------------------

    function edit(change: (current: AvatarCatalogue) => AvatarCatalogue) {
        const current = draftRef.current;
        if (!current) return;
        const next = change(current);
        undoStack.current.push(current);
        if (undoStack.current.length > MAX_UNDO_DEPTH) undoStack.current.shift();
        redoStack.current = [];
        draftRef.current = next;
        setTransient(undefined);
        setDraft(next);
    }

    function updateElement(type: AvatarElementType, id: number, change: Partial<AvatarElement>) {
        edit(current => ({
            ...current,
            elements: {
                ...current.elements,
                [type]: current.elements[type].map(e => e.id === id ? {...e, ...change} : e),
            },
        }));
    }

    function undo() {
        const previous = undoStack.current.pop();
        if (!previous || !draft) return;
        redoStack.current.push(draft);
        setTransient(undefined);
        setDraft(previous);
    }

    function redo() {
        const next = redoStack.current.pop();
        if (!next || !draft) return;
        undoStack.current.push(draft);
        setTransient(undefined);
        setDraft(next);
    }

    const undoRef = useRef(undo);
    undoRef.current = undo;
    const redoRef = useRef(redo);
    redoRef.current = redo;
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (!(e.ctrlKey || e.metaKey) || (e.key !== 'z' && e.key !== 'Z' && e.key !== 'y')) return;
            // Pane hidden (display:none ancestor) → offsetParent is null.
            if (!rootRef.current || rootRef.current.offsetParent === null) return;
            const t = e.target as HTMLElement;
            if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
            e.preventDefault();
            if (e.key === 'y' || e.shiftKey) redoRef.current();
            else undoRef.current();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // ---- uploads ------------------------------------------------------------------

    // Uploads each file and adds them all to the draft as one undo step. A replace
    // (`replacing`) swaps the image under an existing element and keeps its id and fit.
    async function upload(type: AvatarElementType, files: File[], replacing?: AvatarElement) {
        if (!draft || files.length === 0) return;
        setUploading(type);
        // Ids are given out at the end, from the draft as it then stands.
        const added: Omit<AvatarElement, 'id'>[] = [];
        for (const file of files) {
            let prepared: File;
            try {
                prepared = await prepareImageUpload(file);
            } catch (e) {
                setNotification({title: `Could not read ${file.name}`, message: e instanceof Error ? e.message : String(e)});
                continue;
            }
            const dimensions = await readImageDimensions(prepared);
            const formData = new FormData();
            formData.append("file", prepared);
            const result = await postFile(`${apiBindings.api}uploadFile?id=avatar-${type}`, formData, undefined);
            if (!result.success) {
                setNotification({title: `Failed to upload ${file.name}`, message: result.data?.message});
                continue;
            }
            added.push({
                fileName: result.data.fileName as string,
                srcWidth: dimensions?.width,
                srcHeight: dimensions?.height,
                name: replacing ? replacing.name : file.name.replace(/\.[^.]+$/, ''),
                scale: 1, offsetX: 0, offsetY: 0,
            });
            if (replacing) break;
        }
        setUploading(undefined);
        if (added.length === 0) return;

        if (replacing) {
            const image = added[0];
            updateElement(type, replacing.id, {fileName: image.fileName, srcWidth: image.srcWidth, srcHeight: image.srcHeight});
            return;
        }
        const firstId = draftRef.current!.nextIds[type];
        const elements = added.map((image, i) => ({...image, id: firstId + i}));
        edit(current => ({
            ...current,
            elements: {...current.elements, [type]: [...current.elements[type], ...elements]},
            // nextIds only ever grows — deleted ids are never reused.
            nextIds: {...current.nextIds, [type]: current.nextIds[type] + elements.length},
        }));
        const lastId = elements[elements.length - 1].id;
        setSelected({type, id: lastId});
        setPicks(p => ({...p, [type]: lastId}));
    }

    function pickFiles(type: AvatarElementType, replacing?: AvatarElement) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = !replacing;
        input.onchange = () => upload(type, Array.from(input.files ?? []), replacing);
        input.click();
    }

    // ---- element actions ------------------------------------------------------------

    function select(type: AvatarElementType, element: AvatarElement) {
        setTransient(undefined);
        setSelected({type, id: element.id});
        setPicks(p => ({...p, [type]: element.id}));
    }

    function move(type: AvatarElementType, id: number, by: number) {
        if (!draft) return;
        const list = [...draft.elements[type]];
        const from = list.findIndex(e => e.id === id);
        const to = from + by;
        if (from < 0 || to < 0 || to >= list.length) return;
        [list[from], list[to]] = [list[to], list[from]];
        edit(current => ({...current, elements: {...current.elements, [type]: list}}));
    }

    async function deleteElement(type: AvatarElementType, element: AvatarElement) {
        if (!draft) return;
        const list = draft.elements[type];
        const users = usage?.[type]?.[String(element.id)] ?? 0;
        const fallback = list.find(e => e.id !== element.id);
        const proceed = await confirm({
            title: `Delete ${AVATAR_TYPE_LABELS[type]} ${elementLabel(element)}?`,
            body: (
                <div className={'layout vertical gap'} style={{maxWidth: 480}}>
                    <p className={'warning-text'} style={{fontWeight: 600}}>
                        {usage === undefined
                            ? 'Still counting which players use this element.'
                            : usage === null
                                ? 'Could not count which players use this element — assume some do.'
                                : users === 0
                                    ? 'No player avatar uses this element.'
                                    : `${users} player avatar${users === 1 ? '' : 's'} use${users === 1 ? 's' : ''} this element.`}
                    </p>
                    <p>
                        {fallback
                            ? <>Avatars using it will draw <strong>{elementLabel(fallback)}</strong>, the first
                                {' '}{AVATAR_TYPE_LABELS[type].toLowerCase()} in the list, until those players pick again.</>
                            : <>It is the last {AVATAR_TYPE_LABELS[type].toLowerCase()}: avatars using it will show
                                as the <strong>placeholder</strong> until this slot has an element again.</>}
                    </p>
                    <p className={'muted-text'}>
                        Its id is never handed out again. To stop offering it without changing anyone's
                        avatar, use <strong>Hidden</strong> instead. The deletion lands on Save, and reaches
                        players on Publish to Live.
                    </p>
                </div>
            ),
            confirmText: 'Delete element',
            danger: true,
        });
        if (!proceed) return;
        edit(current => ({
            ...current,
            elements: {...current.elements, [type]: current.elements[type].filter(e => e.id !== element.id)},
        }));
        setSelected(undefined);
    }

    function randomize() {
        if (!draft) return;
        const next: AvatarPicks = {};
        for (const type of AVATAR_ELEMENT_TYPES) {
            const offered = draft.elements[type].filter(e => !e.hidden);
            if (offered.length === 0) continue;
            // Optional slots roll "none" as one extra option, like a player leaving it out.
            if (OPTIONAL_AVATAR_TYPES.includes(type) && Math.random() < 1 / (offered.length + 1)) continue;
            next[type] = offered[Math.floor(Math.random() * offered.length)].id;
        }
        setPicks(next);
    }

    function togglePick(type: AvatarElementType) {
        if (!draft) return;
        setPicks(p => {
            if (p[type] !== undefined) {
                const {[type]: _, ...rest} = p;
                return rest;
            }
            const first = draft.elements[type].find(e => !e.hidden) ?? draft.elements[type][0];
            return first ? {...p, [type]: first.id} : p;
        });
    }

    // ---- save / publish -------------------------------------------------------------

    async function save() {
        if (!saved || !draft) return;
        setSaving(true);
        const next: AvatarCatalogue = {...draft, version: saved.version + 1};
        const result = await post(`${apiBindings.api}setAvatars?prod=false`, JSON.stringify(next), undefined);
        setSaving(false);
        if (result.success) {
            setSaved(next);
            setDraft(next);
            return;
        }
        setNotification({
            title: 'Avatar save failed',
            message: result.data?.message ?? 'The catalogue was not saved. Your draft is still here.',
        });
    }

    async function revert() {
        if (!saved) return;
        const proceed = await confirm({
            title: 'Discard unsaved avatar changes?',
            body: <p>The draft goes back to the saved catalogue v{saved.version}.</p>,
            confirmText: 'Discard changes',
            danger: true,
        });
        if (!proceed) return;
        edit(() => saved);
    }

    async function reload() {
        if (dirty && !(await confirm({
            title: 'Reload the avatar catalogue?',
            body: <p>Your unsaved changes are discarded.</p>,
            confirmText: 'Reload',
            danger: true,
        }))) return;
        fetchCatalogue();
        fetchUsage();
    }

    // Publishes what dev holds, so only with nothing unsaved. Confirmed because it
    // impacts live players immediately (and copies element images to prod).
    async function publishLive() {
        if (!saved || dirty) return;
        const proceed = await confirm({
            title: 'Publish avatars to LIVE SERVICE',
            body: <p>{`Publish avatar catalogue v${saved.version} to the LIVE SERVICE? This immediately impacts live players and copies the element images to live storage.`}</p>,
            confirmText: 'Publish to LIVE SERVICE',
            danger: true,
        });
        if (!proceed) return;
        setLoading(true);
        const result = await post(`${apiBindings.api}setAvatars?prod=true`, JSON.stringify(saved), undefined);
        setLoading(false);
        setNotification(result.success
            ? {title: 'Avatars published', message: `The live service now serves avatar catalogue v${saved.version}.`}
            : {title: 'Publish failed', message: result.data?.message});
    }

    // ---- render -----------------------------------------------------------------------

    // The pane is always mounted (editor.view renders every pane and toggles
    // display), so the gate lives here as well as on the sidebar button.
    if (!canEditAvatars) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, gap: 12, padding: 40, textAlign: 'center'}}>
                <p className={'text header'}>Avatars</p>
                <p className={'muted-text'}>The avatar catalogue is global, so editing it needs Tester access.</p>
            </div>
        );
    }

    if (fetchFailed) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, gap: 12, padding: 40, textAlign: 'center'}}>
                <p className={'text header'}>Couldn't load the avatar catalogue</p>
                <p className={'muted-text'} style={{maxWidth: 460}}>
                    Editing is blocked until the catalogue loads — saving over a catalogue we never saw would wipe it.
                </p>
                <button onClick={fetchCatalogue}>Retry</button>
            </div>
        );
    }

    if (!draft || !saved) {
        return (
            <div className={'layout vertical center'} style={{flexGrow: 1, padding: 40}}>
                <p className={'muted-text'}>Loading avatars…</p>
            </div>
        );
    }

    const selectedElement = selected ? draft.elements[selected.type].find(e => e.id === selected.id) : undefined;

    function renderTile(type: AvatarElementType, element: AvatarElement, index: number) {
        const isSelected = selected?.type === type && selected.id === element.id;
        const users = usage?.[type]?.[String(element.id)];
        const tooltip = `${elementLabel(element)} (#${element.id})`
            + (element.hidden ? '\nHidden: not offered to players' : '')
            + (index === 0 ? '\nFirst: drawn in place of a deleted element' : '')
            + (users !== undefined ? `\nUsed by ${users} player${users === 1 ? '' : 's'}` : '');
        return (
            <div key={element.id} className={'layout vertical center'} style={{width: THUMB_SIZE + 4}}>
                <div className={`image-cell${isSelected ? ' selected' : ''}`}
                     title={tooltip}
                     onClick={() => isSelected ? setSelected(undefined) : select(type, element)}
                     style={{position: 'relative', cursor: 'pointer', width: THUMB_SIZE, height: THUMB_SIZE,
                         opacity: element.hidden ? 0.45 : 1}}>
                    <img src={elementImageUrl(element)} alt={elementLabel(element)} draggable={false}
                         style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                    {element.hidden &&
                        <span className={'fa-solid fa-eye-slash'}
                              style={{position: 'absolute', top: 3, right: 3, fontSize: 11}}/>}
                    {index === 0 &&
                        <span className={'fa-solid fa-star'}
                              style={{position: 'absolute', top: 3, left: 3, fontSize: 10, color: 'var(--tint-color)'}}/>}
                </div>
                <p style={{
                    fontSize: 12, width: THUMB_SIZE + 4, textAlign: 'center', lineHeight: 1.2, padding: '1px 2px',
                    boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: isSelected ? 'var(--tint-color)' : 'rgba(255,255,255,0.66)',
                }} title={tooltip}>{elementLabel(element)}</p>
            </div>
        );
    }

    function renderSection(type: AvatarElementType) {
        const list = draft!.elements[type];
        const isCollapsed = collapsed.has(type);
        return (
            <div key={type} className={'layout vertical gap'}
                 onDragOver={e => { e.preventDefault(); setDropTarget(type); }}
                 onDragLeave={() => setDropTarget(t => t === type ? undefined : t)}
                 onDrop={e => {
                     e.preventDefault();
                     setDropTarget(undefined);
                     upload(type, Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')));
                 }}
                 style={{
                     borderRadius: 6, padding: 4,
                     outline: dropTarget === type ? '2px dashed var(--tint-color)' : 'none',
                 }}>
                <div className={'layout horizontal center'} style={{gap: 6}}>
                    <button className={'layout horizontal center gap'}
                            style={{justifyContent: 'flex-start', gap: 8, flexGrow: 1}}
                            onClick={() => {
                                const next = new Set(collapsed);
                                if (next.has(type)) next.delete(type); else next.add(type);
                                setCollapsed(next);
                            }}>
                        <span className={`fa-solid ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}/>
                        <span style={{fontWeight: 600}}>{AVATAR_TYPE_LABELS[type]}</span>
                        <span style={{color: 'rgba(255,255,255,0.66)'}}>({list.length})</span>
                        {OPTIONAL_AVATAR_TYPES.includes(type) &&
                            <span className={'muted-text'} style={{fontSize: 12}}>optional</span>}
                    </button>
                    <button title={`Add ${AVATAR_TYPE_LABELS[type].toLowerCase()} images (or drop files on this section)`}
                            disabled={uploading !== undefined}
                            onClick={() => pickFiles(type)}>
                        <span className={uploading === type ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-plus'}/>
                    </button>
                </div>
                {!isCollapsed &&
                    <div className={'layout horizontal'} style={{flexWrap: 'wrap', gap: 4, alignItems: 'flex-start'}}>
                        {list.map((element, index) => renderTile(type, element, index))}
                        {list.length === 0 &&
                            <p className={'muted-text'} style={{fontStyle: 'italic', padding: 4}}>
                                No elements yet — add images or drop them here.
                            </p>}
                    </div>}
            </div>
        );
    }

    function renderDetails(type: AvatarElementType, element: AvatarElement) {
        const list = draft!.elements[type];
        const index = list.findIndex(e => e.id === element.id);
        const shown = transient && transient.type === type && transient.id === element.id ? transient : element;
        const users = usage?.[type]?.[String(element.id)];
        const commitFit = (change: Partial<TransientFit>) =>
            updateElement(type, element.id, {
                scale: Math.min(AVATAR_SCALE_MAX, Math.max(AVATAR_SCALE_MIN, change.scale ?? shown.scale)),
                offsetX: Math.min(AVATAR_OFFSET_LIMIT, Math.max(-AVATAR_OFFSET_LIMIT, change.offsetX ?? shown.offsetX)),
                offsetY: Math.min(AVATAR_OFFSET_LIMIT, Math.max(-AVATAR_OFFSET_LIMIT, change.offsetY ?? shown.offsetY)),
            });
        return (
            <div className={'layout vertical gap'} style={{gap: 12}}>
                <div className={'layout vertical'} style={{minWidth: 0}}>
                    <p className={'text header'} style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {elementLabel(element)}
                    </p>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        {AVATAR_TYPE_LABELS[type]} #{element.id}
                        {element.srcWidth !== undefined && ` · ${element.srcWidth}×${element.srcHeight}`}
                        {' · '}{users === undefined ? (usage === null ? 'usage unknown' : 'counting…') : `used by ${users}`}
                    </p>
                </div>

                <div className={'layout horizontal center'} style={{gap: 10}}>
                    <p style={{width: 64}}>Name</p>
                    <NameField key={`${type}-${element.id}`} element={element}
                               onCommit={name => updateElement(type, element.id, {name})}/>
                </div>

                <div className={'layout horizontal center'} style={{gap: 10}}>
                    <p style={{width: 64}}>Scale</p>
                    <input type={'range'} min={AVATAR_SCALE_MIN} max={AVATAR_SCALE_MAX} step={0.01}
                           value={shown.scale} style={{flexGrow: 1, minWidth: 0}}
                           onChange={e => setTransient({type, id: element.id, scale: Number(e.target.value),
                               offsetX: shown.offsetX, offsetY: shown.offsetY})}
                           onPointerUp={() => transient && commitFit({})}
                           onKeyUp={() => transient && commitFit({})}/>
                    <NumberInput value={shown.scale} step={'0.01'} displayDecimals={2}
                                 style={{width: 64, textAlign: 'right'}}
                                 onChange={v => commitFit({scale: v})}/>
                </div>

                <div className={'layout horizontal center'} style={{gap: 10}}>
                    <p style={{width: 64}} title={'Fractions of the canvas: x to the right, y up'}>Offset</p>
                    <div className={'layout horizontal center'} style={{gap: 6, flexGrow: 1, justifyContent: 'flex-end'}}>
                        <span className={'muted-text'}>X</span>
                        <NumberInput value={shown.offsetX} step={'0.01'} displayDecimals={3}
                                     style={{width: 72, textAlign: 'right'}} onChange={v => commitFit({offsetX: v})}/>
                        <span className={'muted-text'}>Y</span>
                        <NumberInput value={shown.offsetY} step={'0.01'} displayDecimals={3}
                                     style={{width: 72, textAlign: 'right'}} onChange={v => commitFit({offsetY: v})}/>
                        <button title={'Reset the fit (scale 1, offset 0, 0)'}
                                disabled={element.scale === 1 && element.offsetX === 0 && element.offsetY === 0}
                                onClick={() => updateElement(type, element.id, {scale: 1, offsetX: 0, offsetY: 0})}>
                            <span className={'fa-solid fa-rotate-left'}/>
                        </button>
                    </div>
                </div>

                <label className={'layout horizontal center'} style={{gap: 10, cursor: 'pointer'}}
                       title={'Hidden elements are not offered to players or the randomizer; avatars already using one still draw it'}>
                    <input type={'checkbox'} checked={!!element.hidden}
                           onChange={e => updateElement(type, element.id, {hidden: e.target.checked || undefined})}/>
                    <span>Hidden from players</span>
                </label>

                <div className={'layout horizontal center'} style={{gap: 6}}>
                    <button title={'Move earlier (the first element is drawn in place of a deleted one)'}
                            disabled={index <= 0} onClick={() => move(type, element.id, -1)}>
                        <span className={'fa-solid fa-arrow-left'}/>
                    </button>
                    <button title={'Move later'} disabled={index >= list.length - 1}
                            onClick={() => move(type, element.id, 1)}>
                        <span className={'fa-solid fa-arrow-right'}/>
                    </button>
                    <button title={'Replace the image, keeping this element\'s id and fit'}
                            disabled={uploading !== undefined} onClick={() => pickFiles(type, element)}>
                        <span className={'fa-solid fa-upload'}/>
                    </button>
                    <span style={{flexGrow: 1}}/>
                    <button title={'Delete element'} onClick={() => deleteElement(type, element)}>
                        <span className={'fa-solid fa-trash'}/>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div ref={rootRef} className={'layout horizontal'} style={{flexGrow: 1, minHeight: 0, gap: 8}}>
            {/* Main area: toolbar and stage. */}
            <div className={'layout vertical box'} style={{flexGrow: 1, minWidth: 0, minHeight: 0, padding: 12, gap: 12}}>
                <div className={'layout horizontal center'} style={{gap: 8, flexShrink: 0}}>
                    <div className={'layout vertical'} style={{flexGrow: 1, minWidth: 0}}>
                        <p className={'text header'}>Avatars</p>
                        <p className={'muted-text'} style={{fontSize: 12}}>
                            Global, not per branch · saved v{saved.version}
                            {dirty && <span style={{color: 'var(--tint-color)'}}> · unsaved changes</span>}
                        </p>
                    </div>
                    <button title={'Undo (Ctrl+Z)'} disabled={undoStack.current.length === 0} onClick={undo}>
                        <span className={'fa-solid fa-rotate-left'}/>
                    </button>
                    <button title={'Redo (Ctrl+Y)'} disabled={redoStack.current.length === 0} onClick={redo}>
                        <span className={'fa-solid fa-rotate-right'}/>
                    </button>
                    <button title={'Reload the catalogue from the server'} onClick={reload}>
                        <span className={'fa-solid fa-arrows-rotate'}/>
                    </button>
                    <button title={'Discard unsaved changes'} disabled={!dirty} onClick={revert}>
                        Revert
                    </button>
                    <button title={'Save the draft to the dev catalogue'} disabled={!dirty || saving} onClick={save}>
                        <span className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'}/> Save
                    </button>
                    {canPublishAvatarsLive &&
                        <button className={'export-button production'} disabled={dirty}
                                title={dirty ? 'Save first: Publish sends the saved catalogue' : 'Publish the saved catalogue to live players'}
                                onClick={publishLive}>Publish to Live</button>}
                </div>
                <AvatarStage catalogue={draft} picks={picks} selected={selected} transient={transient}
                             onPreview={setTransient}
                             onCommit={fit => updateElement(fit.type, fit.id,
                                 {scale: fit.scale, offsetX: fit.offsetX, offsetY: fit.offsetY})}
                             onTogglePick={togglePick}
                             onSelectPick={type => {
                                 const id = picks[type];
                                 const element = id === undefined ? undefined : draft.elements[type].find(e => e.id === id);
                                 if (element) setSelected({type, id: element.id});
                             }}
                             onRandomize={randomize}/>
            </div>

            {/* Sidebar: elements per slot on top, the selected element's details below. */}
            <div className={'layout vertical'} style={{width: SIDEBAR_WIDTH, flexShrink: 0, minHeight: 0, gap: 8}}>
                <div className={'layout vertical box'} style={{flexGrow: 1, minHeight: 0, padding: 8, gap: 8, overflowY: 'auto'}}>
                    {AVATAR_ELEMENT_TYPES.map(renderSection)}
                </div>
                <div className={'layout vertical box'} style={{flexShrink: 0, padding: 12, transition: 'none'}}>
                    {selected && selectedElement
                        ? renderDetails(selected.type, selectedElement)
                        : <p className={'muted-text'} style={{textAlign: 'center', padding: 12}}>
                            Select an element to fit it. Drag it on the stage to move it, scroll to scale it.
                        </p>}
                </div>
            </div>
        </div>
    );
}

// The element's editor-only name; one undo step when the field is left, not per keystroke.
function NameField(props: {element: AvatarElement; onCommit(name: string | undefined): void}) {
    const [text, setText] = useState<string | undefined>(undefined);
    const commit = () => {
        if (text === undefined) return;
        const name = text.trim() || undefined;
        setText(undefined);
        if (name !== props.element.name) props.onCommit(name);
    };
    return (
        <input value={text ?? props.element.name ?? ''} placeholder={`#${props.element.id}`}
               style={{flexGrow: 1, minWidth: 0}}
               onChange={e => setText(e.target.value)}
               onBlur={commit}
               onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}/>
    );
}

export default AvatarEditorView;
